import { useSyncExternalStore, useMemo } from 'react';

export type WindowSize = {
  width: number;
  height: number;
};

// Live viewport check for event handlers (same `xs` breakpoint as useWindowSize),
// so callers can read mobile state at click time without subscribing to resize.
export const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= 768;

interface UseWindowSizeResult {
  windowSize: WindowSize;
  windowWidth: {
    xs: boolean;
    md: boolean;
    lg: boolean;
    xsDown: boolean;
    '1200Down': boolean;
    '1040Down': boolean;
  };
  isMobileTabletView: boolean;
  isMobileView: boolean;
}

// Single app-wide viewport store shared by every useWindowSize instance.
//
// Read the viewport synchronously on the very first render via useSyncExternalStore
// instead of measuring in a post-mount effect. The old effect-based approach seeded
// state at width 0, so every fresh mount painted the mobile/compact branch for one
// frame before the effect corrected it — a visible layout flash on any remount
// (e.g. the Experiments listing re-mounting after a filter/sort/search/tab switch).
//
// Width/height are cached in module vars and refreshed only on `resize`, with ONE
// shared resize listener for the whole app. This hook is called per listing row, so
// a per-instance listener + a per-render `window.innerWidth` read (which can force
// layout reflow) would multiply across every row; the shared cache avoids both.
// getServerSnapshot keeps SSR at 0, matching prior output, so there is no hydration
// mismatch.
let cachedWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
let cachedHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
const listeners = new Set<() => void>();

const handleResize = () => {
  cachedWidth = window.innerWidth;
  cachedHeight = window.innerHeight;
  listeners.forEach((listener) => listener());
};

const subscribe = (onChange: () => void) => {
  // Attach the single real listener on the first subscriber and re-sync the cache,
  // in case the viewport changed between module load and the first mount.
  if (listeners.size === 0) {
    cachedWidth = window.innerWidth;
    cachedHeight = window.innerHeight;
    window.addEventListener('resize', handleResize);
  }
  listeners.add(onChange);

  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      window.removeEventListener('resize', handleResize);
    }
  };
};

// Snapshots return cached primitives so React compares them by value (a fresh object
// each call would loop) without touching the DOM on every render.
const getWidthSnapshot = () => cachedWidth;
const getHeightSnapshot = () => cachedHeight;
const getServerSnapshot = () => 0;

export const useWindowSize = (): UseWindowSizeResult => {
  const width = useSyncExternalStore(subscribe, getWidthSnapshot, getServerSnapshot);
  const height = useSyncExternalStore(subscribe, getHeightSnapshot, getServerSnapshot);

  const windowSize = useMemo<WindowSize>(() => ({ width, height }), [width, height]);

  const windowWidth = useMemo(() => {
    return {
      xs: width <= 768,
      md: 768 < width && width <= 1024,
      lg: width > 1024,
      xsDown: width < 768,
      '1200Down': width < 1200,
      '1040Down': width < 1040,
    };
  }, [width]);

  const isMobileTabletView = !windowWidth.lg;
  const isMobileView = windowWidth.xs;

  return { windowSize, windowWidth, isMobileTabletView, isMobileView };
};
